import React, { useState, useEffect } from 'react';
import { frontendDb as db } from '../../lib/frontend-db';

interface VereineSettings {
  wiesen_title: string;
  wiesen_description: string;
  wiesen_image: string;
  wiesen_url: string;
  bieber_title: string;
  bieber_description: string;
  bieber_image: string;
  bieber_url: string;
}

const VereinePage = () => {
  const [settings, setSettings] = useState<VereineSettings>({
    wiesen_title: 'Liederkranz Wiesen',
    wiesen_description: 'Entdecken Sie den Liederkranz Wiesen und erfahren Sie mehr über diesen Teil unseres Chor-Zusammenschlusses.',
    wiesen_image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=500&h=300&fit=crop',
    wiesen_url: 'https://www.liederkranz-wiesen.de',
    bieber_title: 'Liederkranz Bieber',
    bieber_description: 'Erfahren Sie mehr über den Liederkranz Bieber und unsere lange Tradition seit 1847.',
    bieber_image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=300&fit=crop',
    bieber_url: 'https://www.liederkranz-bieber.de'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pageTitle, setPageTitle] = useState('Vereine');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const siteSettings = await db.getSettings();
      if (siteSettings) {
        setPageTitle((siteSettings as any).vereine_title || 'Unsere Vereine');
        // Extract vereine settings from site settings
        const vereineSettings = {
          wiesen_title: (siteSettings as any).wiesen_title || settings.wiesen_title,
          wiesen_description: (siteSettings as any).wiesen_description || settings.wiesen_description,
          wiesen_image: (siteSettings as any).wiesen_image || settings.wiesen_image,
          wiesen_url: (siteSettings as any).wiesen_url || settings.wiesen_url,
          bieber_title: (siteSettings as any).bieber_title || settings.bieber_title,
          bieber_description: (siteSettings as any).bieber_description || settings.bieber_description,
          bieber_image: (siteSettings as any).bieber_image || settings.bieber_image,
          bieber_url: (siteSettings as any).bieber_url || settings.bieber_url,
        };
        setSettings(vereineSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Fehler beim Laden der Vereine-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await db.updateSettings(settings);
      window.location.reload();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Fehler beim Speichern der Vereine-Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'wiesen' | 'bieber') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imageUrl = await db.storeImage(file);
      setSettings(prev => ({
        ...prev,
        [`${type}_image`]: imageUrl
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Fehler beim Hochladen des Bildes');
    }
  };

  if (loading) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          Vereine-Einstellungen wurden erfolgreich gespeichert!
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">{pageTitle}</h2>
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Liederkranz Wiesen */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Liederkranz Wiesen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Titel</label>
                <input
                  type="text"
                  value={settings.wiesen_title}
                  onChange={(e) => setSettings(prev => ({ ...prev, wiesen_title: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">URL</label>
                <input
                  type="url"
                  value={settings.wiesen_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, wiesen_url: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  required
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Beschreibung</label>
              <textarea
                value={settings.wiesen_description}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_description: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Bild</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'wiesen')}
                className="mt-1 block w-full"
              />
              {settings.wiesen_image && (
                <div className="mt-2">
                  <img
                    src={settings.wiesen_image}
                    alt="Wiesen Vorschau"
                    className="w-full max-h-48 object-cover rounded"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Liederkranz Bieber */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Liederkranz Bieber</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Titel</label>
                <input
                  type="text"
                  value={settings.bieber_title}
                  onChange={(e) => setSettings(prev => ({ ...prev, bieber_title: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">URL</label>
                <input
                  type="url"
                  value={settings.bieber_url}
                  onChange={(e) => setSettings(prev => ({ ...prev, bieber_url: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  required
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Beschreibung</label>
              <textarea
                value={settings.bieber_description}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_description: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Bild</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'bieber')}
                className="mt-1 block w-full"
              />
              {settings.bieber_image && (
                <div className="mt-2">
                  <img
                    src={settings.bieber_image}
                    alt="Bieber Vorschau"
                    className="w-full max-h-48 object-cover rounded"
                  />
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VereinePage;

